import { Link, useSearchParams } from 'react-router-dom';
import { Carte, Chargement, EnteteEcran, MessageErreur } from '../components/ui/Primitives';
import { Onglets, PanneauOnglet } from '../components/ui/Onglets';
import { OngletAbsences } from '../components/parametrage/OngletAbsences';
import { OngletEmargement } from '../components/parametrage/OngletEmargement';
import { OngletAlertes } from '../components/parametrage/OngletAlertes';
import { OngletModeles } from '../components/parametrage/OngletModeles';
import { OngletHistorique } from '../components/parametrage/OngletHistorique';
import { OngletMarque } from '../components/parametrage/OngletMarque';
import { OngletAccessibilite } from '../components/parametrage/OngletAccessibilite';
import { OngletReclamations } from '../components/parametrage/OngletReclamations';
import { OngletMotsBloques } from '../components/parametrage/OngletMotsBloques';
import { ErreurApi } from '../lib/api';
import { useParametrage } from '../lib/useParametrage';

type Onglet =
  | 'absences'
  | 'emargement'
  | 'alertes'
  | 'reclamations'
  | 'mots'
  | 'modeles'
  | 'marque'
  | 'accessibilite'
  | 'historique';

const ONGLETS: readonly { cle: Onglet; libelle: string }[] = [
  { cle: 'absences', libelle: 'Absences' },
  { cle: 'emargement', libelle: 'Émargement' },
  { cle: 'alertes', libelle: 'Alertes' },
  { cle: 'reclamations', libelle: 'Réclamations' },
  { cle: 'mots', libelle: 'Mots bloqués' },
  { cle: 'modeles', libelle: 'Modèles de documents' },
  { cle: 'marque', libelle: 'Marque blanche' },
  { cle: 'accessibilite', libelle: 'Accessibilité' },
  { cle: 'historique', libelle: 'Historique' },
];

function ongletDe(valeur: string | null): Onglet {
  return ONGLETS.some((onglet) => onglet.cle === valeur) ? (valeur as Onglet) : 'absences';
}

/**
 * Paramétrage de la plateforme (phase 5, TF-435) : les réglages généraux de
 * l'organisme — seuils d'absences, tolérance de retard, règles et délais des
 * alertes, mots bloqués (TF-484), modèles de documents, marque blanche (TF-436) —, réservés à la
 * direction, chaque modification tracée dans l'historique. `?onglet=` ouvre
 * une section.
 */
export function DirParametresPage(): JSX.Element {
  const [parametres, setParametres] = useSearchParams();
  const onglet = ongletDe(parametres.get('onglet'));
  const { data, isLoading, error } = useParametrage();

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <EnteteEcran
        titre="Paramétrage de la plateforme"
        sousTitre="Seuils, tolérance d’émargement, alertes, réclamations, mots bloqués, modèles de documents, marque blanche et référent handicap · réglés par la direction, chaque modification tracée"
      />

      <Onglets
        onglets={ONGLETS}
        actif={onglet}
        onChanger={(cle) => setParametres({ onglet: cle }, { replace: true })}
        etiquette="Sections du paramétrage"
        idPanneau="panneau-parametrage"
      />

      <PanneauOnglet id="panneau-parametrage" actif={onglet} className="space-y-6">
        {onglet === 'historique' ? (
          <OngletHistorique />
        ) : (
          <>
            {isLoading && <Chargement libelle="Chargement des réglages…" />}
            {error && (
              <MessageErreur
                message={error instanceof ErreurApi ? error.message : 'Les réglages n’ont pas pu être chargés.'}
              />
            )}
            {data && onglet === 'absences' && <OngletAbsences absences={data.absences} />}
            {data && onglet === 'emargement' && (
              <OngletEmargement tolerance={data.emargement.tolerance} rappels={data.emargement.rappels} />
            )}
            {data && onglet === 'alertes' && <OngletAlertes alertes={data.alertes} />}
            {data && onglet === 'reclamations' && <OngletReclamations reclamations={data.reclamations} />}
            {data && onglet === 'mots' && <OngletMotsBloques motsBloques={data.motsBloques} />}
            {data && onglet === 'modeles' && <OngletModeles modeles={data.modeles} />}
            {data && onglet === 'marque' && <OngletMarque marque={data.marque} />}
            {data && onglet === 'accessibilite' && <OngletAccessibilite accessibilite={data.accessibilite} />}
          </>
        )}
      </PanneauOnglet>

      {data && data.autresReglages.length > 0 && (
        <Carte titre="Autres réglages de la plateforme">
          {data.autresReglages.map((reglage) => (
            <div
              key={reglage.ecran}
              className="flex flex-col gap-1 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-6"
            >
              <div className="min-w-0">
                <div className="text-sm font-semibold text-codialis-navy">{reglage.libelle}</div>
                <div className="text-[11px] text-codialis-gray">
                  {reglage.detail} · {reglage.tenuPar}
                </div>
              </div>
              <Link
                to={reglage.lien}
                className="inline-flex min-h-[44px] shrink-0 items-center text-xs font-semibold text-codialis-sky hover:underline"
              >
                Ouvrir
              </Link>
            </div>
          ))}
        </Carte>
      )}
    </div>
  );
}
